import {
  DOWNLOAD_PLATFORMS,
  formatBytes,
  safeDownloadUrl,
  type DownloadPlatform,
  type ReleaseState,
  type StoreBrand,
} from './release';
import { fill, type Lang, type Strings } from './strings';

type Child = Node | string | null | false | undefined;

/** Tiny element helper. Text is always set as text nodes, never parsed as HTML. */
export function h(
  tag: string,
  attrs: Record<string, string | undefined> = {},
  ...children: Child[]
): HTMLElement {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) if (v !== undefined) el.setAttribute(k, v);
  for (const c of children) if (c) el.append(c);
  return el;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Icon paths (Lucide geometry, 24×24, stroke-based) so the page needs no icon font or images. */
const ICONS = {
  phone: ['M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z', 'M12 18h.01'],
  tv: ['M4 7h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z', 'm17 2-5 5-5-5'],
  stick: ['M9 2h6a1 1 0 0 1 1 1v13a4 4 0 0 1-8 0V3a1 1 0 0 1 1-1Z', 'M12 6h.01', 'M12 22v-2'],
  download: ['M12 15V3', 'm7 10 5 5 5-5', 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'],
  globe: [
    'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z',
    'M2 12h20',
    'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z',
  ],
  chevron: ['m6 9 6 6 6-6'],
  alert: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z', 'M12 8v4', 'M12 16h.01'],
  refresh: [
    'M3 12a9 9 0 0 1 15-6.7L21 8',
    'M21 3v5h-5',
    'M21 12a9 9 0 0 1-15 6.7L3 16',
    'M8 16H3v5',
  ],
  shield: [
    'M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1Z',
  ],
} as const;

export function icon(name: keyof typeof ICONS, cls = 'icon'): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', cls);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  for (const d of ICONS[name]) {
    const p = document.createElementNS(SVG_NS, 'path');
    p.setAttribute('d', d);
    svg.append(p);
  }
  return svg;
}

const platformIcon: Record<DownloadPlatform, keyof typeof ICONS> = {
  android_mobile: 'phone',
  android_tv: 'tv',
  fire_tv: 'stick',
};

export interface ViewState {
  lang: Lang;
  t: Strings;
  brand: StoreBrand | null;
  releases: Partial<Record<DownloadPlatform, ReleaseState>> | null;
  origin: string;
  onToggleLanguage: () => void;
  onRetry: () => void;
}

const locale = (lang: Lang) => (lang === 'ar' ? 'ar-u-nu-latn' : 'en');

function releaseCard(
  platform: DownloadPlatform,
  state: ReleaseState | undefined,
  v: ViewState,
): HTMLElement {
  const { t } = v;
  const head = h(
    'div',
    { class: 'card-head' },
    h('span', { class: 'platform-icon' }, icon(platformIcon[platform])),
    h('h2', {}, t[platform]),
  );
  if (!state)
    return h(
      'section',
      { class: 'card', 'aria-busy': 'true' },
      head,
      h('p', { class: 'sr-only' }, t.loading),
      h(
        'div',
        { class: 'skeleton-group', 'aria-hidden': 'true' },
        h('span', { class: 'skeleton w-60' }),
        h('span', { class: 'skeleton button-skeleton' }),
      ),
    );
  if (state.kind === 'none')
    return h(
      'section',
      { class: 'card is-empty' },
      head,
      h('p', { class: 'state' }, t.unavailable),
    );
  if (state.kind === 'error')
    return h(
      'section',
      { class: 'card is-error' },
      head,
      h('p', { class: 'state error' }, icon('alert', 'icon sm'), h('span', {}, t.loadError)),
    );

  const r = state.release;
  const url = safeDownloadUrl(r.downloadUrl, v.origin);
  const size = formatBytes(r.fileSizeBytes, locale(v.lang));
  const date = new Intl.DateTimeFormat(locale(v.lang), { dateStyle: 'medium' }).format(
    new Date(r.publishedAt),
  );
  const notes =
    (v.lang === 'ar'
      ? (r.releaseNotes.ar ?? r.releaseNotes.en)
      : (r.releaseNotes.en ?? r.releaseNotes.ar)) ?? null;

  const disclosure = (label: string, body: HTMLElement) =>
    h(
      'details',
      {},
      h('summary', {}, h('span', {}, label), icon('chevron', 'icon sm chevron')),
      body,
    );

  return h(
    'section',
    { class: 'card' },
    head,
    h(
      'p',
      { class: 'meta' },
      h('span', { class: 'chip', dir: 'ltr' }, fill(t.version, { v: r.version })),
      size && h('span', { class: 'chip' }, fill(t.size, { s: size })),
      h('span', { class: 'chip' }, fill(t.published, { d: date })),
    ),
    url
      ? h(
          'a',
          { class: 'button', href: url, download: '', rel: 'noopener' },
          icon('download'),
          h('span', {}, t.download),
        )
      : h('p', { class: 'state' }, t.unavailable),
    notes && disclosure(t.notes, h('p', { class: 'notes' }, notes)),
    r.sha256 && disclosure(t.checksum, h('code', { class: 'hash', dir: 'ltr' }, r.sha256)),
  );
}

export function render(root: HTMLElement, v: ViewState): void {
  const { t, brand } = v;
  document.documentElement.lang = v.lang;
  document.documentElement.dir = v.lang === 'ar' ? 'rtl' : 'ltr';
  document.title = brand ? `${brand.name} · ${t.title}` : t.title;
  const style = document.documentElement.style;
  if (brand) {
    style.setProperty('--brand', brand.primary);
    style.setProperty('--bg', brand.background);
    style.setProperty('--fg', brand.text);
  }

  const langButton = h(
    'button',
    { type: 'button', class: 'pill', lang: v.lang === 'ar' ? 'en' : 'ar' },
    icon('globe', 'icon sm'),
    h('span', {}, t.language),
  );
  langButton.addEventListener('click', v.onToggleLanguage);

  const anyError = v.releases && Object.values(v.releases).some((s) => s?.kind === 'error');
  const retry = anyError
    ? h(
        'button',
        { type: 'button', class: 'button secondary retry' },
        icon('refresh', 'icon sm'),
        h('span', {}, t.retry),
      )
    : null;
  retry?.addEventListener('click', v.onRetry);

  const logo = brand?.logoUrl
    ? h('img', { src: brand.logoUrl, alt: '', class: 'logo', referrerpolicy: 'no-referrer' })
    : null;

  const children: Child[] = [
    h(
      'header',
      { class: 'top' },
      h(
        'div',
        { class: 'brand' },
        logo ?? h('span', { class: 'mark', 'aria-hidden': 'true' }, icon('tv', 'icon sm')),
        h('strong', {}, brand?.name ?? ''),
      ),
      langButton,
    ),
    h(
      'section',
      { class: 'hero' },
      h('h1', {}, t.title),
      h('p', { class: 'lead' }, t.subtitle),
      brand &&
        h(
          'p',
          { class: 'code' },
          h('span', {}, fill(t.storeCode, { code: '' })),
          h('bdi', { dir: 'ltr' }, brand.code),
        ),
    ),
    h(
      'div',
      { class: 'grid' },
      ...DOWNLOAD_PLATFORMS.map((p) => releaseCard(p, v.releases?.[p], v)),
    ),
    retry,
    h(
      'section',
      { class: 'how' },
      h('h2', {}, t.howTitle),
      h(
        'ol',
        {},
        h('li', {}, t.howPhone),
        h('li', {}, t.howTv),
        h('li', {}, t.howFire),
        h('li', {}, t.storeHint),
      ),
    ),
    h('footer', {}, icon('shield', 'icon sm'), h('p', {}, t.legal)),
  ];
  root.replaceChildren(...children.filter((c): c is Node | string => Boolean(c)));
}
